import { useRender } from '@base-ui/react/use-render'
import { forwardRef } from 'react'
import { buttonClasses } from './styles/button-classes'
import type { ButtonSize, ButtonVariant } from './styles/button-variant'
import type { ActionTone } from './styles/tones'

type ButtonLinkShared = useRender.ComponentProps<'a'> & {
  /** De qué habla el destino. Default `primary`, como `Button`. */
  tone?: ActionTone
  /** Cuánto pesa. Default `solid`, como `Button`. */
  variant?: ButtonVariant
  size?: ButtonSize
}

/**
 * El destino llega por una sola vía: `href` (URL externa) o `render` (el `Link` de un router). El tipo
 * exige una y excluye la otra; que el elemento de `render` tenga destino lo sostiene el call site.
 */
export type ButtonLinkProps =
  | (ButtonLinkShared & { href: string; render?: undefined })
  | (ButtonLinkShared & { href?: undefined; render: NonNullable<useRender.ComponentProps<'a'>['render']> })

/**
 * Enlace con aspecto de botón: emite un `<a>` real, sin `disabled`. En una SPA el destino va por
 * `render={<Link/>}`; trigger de `Tooltip` sí, de `Menu`/`Popover`/`Dialog` no. Ver
 * `COMPOSITION.md § Navegación`.
 */
// `forwardRef` es un shim de React 18: la capa corre React 19, donde la `ref` ya viaja dentro de
// `props`. Se retira con el codemod `react/19/remove-forward-ref` cuando el último consumidor
// migre (ADR-0028).
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { tone = 'primary', variant = 'solid', size = 'md', className, render, ...props },
  ref,
) {
  return useRender({
    render,
    ref,
    defaultTagName: 'a',
    props: { ...props, className: buttonClasses(tone, variant, size, false, className) },
  })
})

ButtonLink.displayName = 'ButtonLink'
